import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Check, FileText, Upload, Stethoscope, Clock, Download, Eye,
  CalendarX, Save, CheckCircle2, MessageSquare, UserRound, BadgeCheck,
  GraduationCap, Briefcase, Languages, IndianRupee, UsersRound, Send, X,
  LayoutDashboard, ListChecks, ChevronDown, Megaphone, Home, LogOut, Menu as MenuIcon,
  Building2, Sparkles, MapPin, ShieldCheck, Lock, Phone, Mail, Plus, Trash2,
  ExternalLink, Image as ImageIcon, AlertCircle, Hospital, Pencil,
} from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { COUNTRIES } from "@/data/countries";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import { useKeyNav } from "@/lib/useKeyNav";
import PortalShell from "@/components/portal/PortalShell";
import Calendar from "@/components/portal/Calendar";
import Video from "@/components/portal/Video";
import Chat from "@/components/portal/Chat";
import Inbox from "@/components/portal/Inbox";
import MailView from "@/components/portal/PortalMail";
import NotificationsBell from "@/components/portal/NotificationsBell";
import { useMessaging } from "@/context/MessagingContext";
import { AddToCalendar, CalendarSubscribe } from "@/components/portal/CalendarSync";
import DoctorMyAppointments, { StampUploader } from "./MyAppointments";
import CallbackRequestModal from "@/components/site/CallbackRequestModal";

const SPECIALTIES = ["Oncology", "Cardiology", "Neurology", "Orthopaedics", "Nephrology",
  "Gastroenterology", "Endocrinology", "Pulmonology", "OBGYN", "Dermatology", "Ophthalmology"];

const DOC_FIELDS = [
  { key: "id", label: "Identity Card (Aadhaar / Passport)", req: true },
  { key: "degree", label: "Degree Certificate", req: true },
  { key: "mbbsreg", label: "MBBS Registration Certificate", req: true },
  { key: "pgdeg", label: "PG Degree Certificate", req: false },
  { key: "pgreg", label: "PG Registration Certificate", req: false },
  { key: "ssdeg", label: "Superspecialty Degree Certificate", req: false },
  { key: "ssreg", label: "Superspecialty Registration Certificate", req: false },
];
const DOC_LABEL = Object.fromEntries(DOC_FIELDS.map(d => [d.key, d.label]));

/* ------------------------------ Doctor Auth ------------------------------ */
function DoctorAuth({ onDone }) {
  const { refresh } = useAuth();
  const [mode, setMode] = useState("login");
  const [stage, setStage] = useState("form"); // form | otp | docs | pending
  const [form, setForm] = useState({ name: "", mobile: "", email: "", password: "", reg_no: "", specialty: "", country: "", dial_code: "" });
  const [otp, setOtp] = useState(""); const [devOtp, setDevOtp] = useState("");
  const [newPass, setNewPass] = useState("");
  const [docToken, setDocToken] = useState("");
  const [files, setFiles] = useState({});
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [cbOpen, setCbOpen] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const login = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/login", { email: form.email, password: form.password });
      const u = await refresh();
      if (u?.role !== "doctor") { setErr("This is the doctor portal."); return; }
      onDone();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendOtp = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/send-otp", { purpose: "register", role: "doctor", ...form });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStage("otp");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email: form.email, code: otp });
      setDocToken(data.doc_token); setStage("docs");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitDocs = async () => {
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(files).forEach(([k, f]) => { if (f) { fd.append("files", f); fd.append("types", k); } });
      await api.post("/auth/doctor-documents", fd, { headers: { Authorization: `Bearer ${docToken}` } });
      setStage("pending");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendReset = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/send-otp", { purpose: "reset", role: "doctor", email: form.email });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStage("otp");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyReset = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/verify-otp", { email: form.email, code: otp, new_password: newPass });
      toast("Password updated — please sign in.");
      setMode("login"); setStage("form"); setOtp(""); setNewPass("");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (stage === "pending") return (
    <div className="card" style={{ maxWidth: 520, margin: "30px auto", textAlign: "center" }}>
      <CheckCircle2 size={42} color="#1D9E75" />
      <h2 className="h" style={{ fontSize: 22 }}>Application submitted</h2>
      <p className="lead">Thank you. Our team will verify your credentials and approve your account. You’ll be able to sign in once approved.</p>
      <button className="btn btn-ink" onClick={() => { setMode("login"); setStage("form"); }}>Back to sign in</button>
    </div>
  );

  return (
    <div className="auth">
      <div className="auth-art">
        <div className="logo" style={{ marginBottom: 22 }}>2</div>
        <h2>Lend your expertise. Give patients clarity when it matters most.</h2>
        <p>Join a panel of registered specialists offering structured second opinions and video consultations.</p>
        {["Verified, NMC-registered panel", "You control your availability", "Secure, compliant platform"].map(t => (
          <div className="pt" key={t}><Check size={16} /> {t}</div>
        ))}
      </div>

      <div className="auth-form">
        <div className="tabs">
          <button className={`tab ${mode === "login" ? "on" : ""}`} onClick={() => { setMode("login"); setStage("form"); }} data-testid="doc-tab-login">Sign in</button>
          <button className={`tab ${mode === "register" ? "on" : ""}`} onClick={() => { setMode("register"); setStage("form"); }} data-testid="doc-tab-register">Register</button>
        </div>
        {err && <p className="err" data-testid="doc-auth-error">{err}</p>}
        {devOtp && stage === "otp" && <div className="demohint">Demo mode — your code is <b>{devOtp}</b></div>}

        {mode === "login" && (
          <>
            <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="doc-login-email" /></div>
            <div className="field"><label>Password</label><input className="in" type="password" value={form.password} onChange={set("password")} data-testid="doc-login-password" /></div>
            <button className="btn btn-ink full" onClick={login} disabled={busy} data-testid="doc-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
            <p className="note" style={{ cursor: "pointer" }} onClick={() => { setMode("forgot"); setStage("form"); setErr(""); }} data-testid="doc-forgot-link">Forgot your password?</p>
          </>
        )}

        {mode === "forgot" && stage === "form" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Enter your registered email and we’ll send a verification code.</p>
            <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="doc-forgot-email" /></div>
            <button className="btn btn-ink full" onClick={sendReset} disabled={busy}>{busy ? "Sending…" : "Send code"}</button>
            <p className="note" style={{ cursor: "pointer" }} onClick={() => { setMode("login"); setStage("form"); }}>Back to sign in</p>
          </>
        )}

        {mode === "forgot" && stage === "otp" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Enter the code sent to <b>{form.email}</b> and choose a new password.</p>
            <div className="field"><label>Verification code</label><input className="in" maxLength={6} value={otp} onChange={e => setOtp(e.target.value)} data-testid="doc-reset-otp" /></div>
            <div className="field"><label>New password</label><input className="in" type="password" value={newPass} onChange={e => setNewPass(e.target.value)} data-testid="doc-reset-newpass" /></div>
            <button className="btn btn-green full" onClick={verifyReset} disabled={busy} data-testid="doc-reset-verify">{busy ? "Updating…" : "Update password"}</button>
          </>
        )}

        {mode === "register" && stage === "form" && (
          <>
            <div className="row2">
              <div className="field"><label>Full name</label><input className="in" value={form.name} onChange={set("name")} data-testid="doc-name" /></div>
              <div className="field"><label>Country</label>
                <select className="in" value={form.country} onChange={(e) => { const c = COUNTRIES.find(x => x.name === e.target.value); setForm({ ...form, country: e.target.value, dial_code: c ? c.dial : form.dial_code }); }} data-testid="doc-country">
                  <option value="">Select country</option>
                  {COUNTRIES.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="field"><label>Mobile</label>
              <div className="phone-row">
                <select className="in phone-code" value={form.dial_code} onChange={set("dial_code")} data-testid="doc-dial-code">
                  <option value="">Code</option>
                  {COUNTRIES.filter(c => c.dial !== "+").map(c => <option key={c.code} value={c.dial}>{c.dial} {c.code}</option>)}
                </select>
                <input className="in" value={form.mobile} onChange={set("mobile")} placeholder="Phone number" data-testid="doc-mobile" />
              </div>
            </div>
            <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="doc-email" /></div>
            <div className="row2">
              <div className="field"><label>NMC reg. no.</label><input className="in" value={form.reg_no} onChange={set("reg_no")} placeholder="e.g. KMC 119333" data-testid="doc-regno" /></div>
              <div className="field"><label>Specialty</label>
                <select className="in" value={form.specialty} onChange={set("specialty")} data-testid="doc-specialty">
                  <option value="">Select</option>{SPECIALTIES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="field"><label>Password (min 8)</label><input className="in" type="password" value={form.password} onChange={set("password")} data-testid="doc-password" /></div>
            <button className="btn btn-green full" onClick={sendOtp} disabled={busy} data-testid="doc-reg-submit">{busy ? "Sending…" : "Verify email"}</button>
          </>
        )}

        {mode === "register" && stage === "otp" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Enter the code sent to <b>{form.email}</b>.</p>
            <div className="field"><label>Verification code</label><input className="in" maxLength={6} value={otp} onChange={e => setOtp(e.target.value)} data-testid="doc-otp" /></div>
            <button className="btn btn-green full" onClick={verify} disabled={busy} data-testid="doc-otp-verify">{busy ? "Verifying…" : "Continue to documents"}</button>
          </>
        )}

        {mode === "register" && stage === "docs" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Upload your credentials for verification.</p>
            {DOC_FIELDS.map(d => (
              <div className="docrow" key={d.key}>
                <FileText size={18} color="#185FA5" />
                <div className="m"><h4>{d.label}<span className={`req ${d.req ? "r" : "o"}`}>{d.req ? "required" : "optional"}</span></h4>
                  <p>{files[d.key] ? files[d.key].name : "No file chosen"}</p></div>
                <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                  <Upload size={13} /> {files[d.key] ? "Change" : "Upload"}
                  <input type="file" hidden onChange={e => setFiles({ ...files, [d.key]: e.target.files[0] })} data-testid={`doc-file-${d.key}`} />
                </label>
              </div>
            ))}
            <button className="btn btn-ink full" style={{ marginTop: 10 }} onClick={submitDocs} disabled={busy} data-testid="doc-submit-docs">{busy ? "Submitting…" : "Submit for verification"}</button>
          </>
        )}

        <div className="auth-callback-cta" data-testid="auth-callback-cta">
          <span>Need help signing in or registering?</span>
          <button type="button" className="auth-callback-btn" onClick={() => setCbOpen(true)} data-testid="auth-callback-trigger">
            Request a callback from customer support
          </button>
        </div>
      </div>
      {cbOpen && <CallbackRequestModal role="doctor" onClose={() => setCbOpen(false)} />}
    </div>
  );
}

/* --------------------------- Availability manager ------------------------ */
function Availability({ assignContext, onAssigned }) {
  const [date, setDate] = useState(assignContext?.date || "");
  const [data, setData] = useState({ blocked: [], booked: [], peer: [], slots: [] });

  useEffect(() => { if (date) api.get(`/doctor/availability?date=${date}`).then(r => setData(r.data)); }, [date]);

  const toggle = (t) => {
    if (data.booked.includes(t) || data.peer.includes(t)) return;
    if (assignContext) return; // when assigning, click triggers assignment
    const blocked = data.blocked.includes(t) ? data.blocked.filter(x => x !== t) : [...data.blocked, t];
    setData({ ...data, blocked });
  };
  const save = async () => { await api.post("/doctor/availability", { date, blocked: data.blocked }); toast("Availability saved ✓"); };
  const assign = async (t) => {
    if (data.booked.includes(t) || data.peer.includes(t) || data.blocked.includes(t)) {
      toast("Pick a free (white) slot to give doctor " + assignContext.requesterName); return;
    }
    if (!window.confirm(`Assign ${date} ${t} to ${assignContext.requesterName} for a peer consultation?`)) return;
    try {
      await api.post(`/doctor/peer/requests/${assignContext.requestId}/assign`, { date, time: t });
      toast("Slot assigned ✓ — doctor will see it on their calendar.");
      onAssigned?.();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="card">
      <h3 className="h" style={{ fontSize: 18, marginTop: 0 }}>{assignContext ? `Assign a slot to ${assignContext.requesterName}` : "Manage availability"}</h3>
      <p className="lead" style={{ marginBottom: 12 }}>
        {assignContext
          ? "Pick any free (white) slot. The slot will be reserved for this peer consult without touching your patient calendar."
          : "Block any slots you’re unavailable for. Patients won’t be able to book these."}
      </p>
      <div className="slot-legend" data-testid="doc-slot-legend">
        <span><i className="lg lg-open" /> Free</span>
        <span><i className="lg lg-booked" /> Patient booked</span>
        <span><i className="lg lg-peer" /> Doctor (peer)</span>
        <span><i className="lg lg-blocked" /> Blocked</span>
      </div>
      <Calendar selected={date} onSelect={setDate} />
      {date && (
        <>
          <div className="slots" data-testid="doc-slots">
            {data.slots.map(t => {
              const booked = data.booked.includes(t);
              const peer = data.peer.includes(t);
              const blocked = data.blocked.includes(t);
              const cls = peer ? "peer" : booked ? "taken" : blocked ? "blocked" : "open";
              return (
                <button
                  key={t}
                  className={`slot ${cls}`}
                  disabled={booked || peer}
                  onClick={() => (assignContext ? assign(t) : toggle(t))}
                  data-testid={`doc-slot-${t}`}
                  title={peer ? "Reserved for a peer consultation" : booked ? "Booked by a patient" : blocked ? "You marked this blocked" : "Free"}
                >
                  {t}
                </button>
              );
            })}
          </div>
          {!assignContext && (
            <button className="btn btn-ink full" style={{ marginTop: 14 }} onClick={save} data-testid="doc-save-availability"><Save size={15} /> Save availability</button>
          )}
        </>
      )}
    </div>
  );
}

/* --------------------------- Peer consultations -------------------------- */
function PeerConsult({ onAssignSlot }) {
  const [tab, setTab] = useState("incoming");
  const [data, setData] = useState({ incoming: [], outgoing: [] });
  const [showAsk, setShowAsk] = useState(false);
  const [declineFor, setDeclineFor] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/doctor/peer/requests"); setData(data); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const pendingIn = data.incoming.filter(r => r.status === "pending").length;
  const pendingOut = data.outgoing.filter(r => r.status === "pending").length;
  const list = tab === "incoming" ? data.incoming : data.outgoing;

  const decline = async (rid, reason) => {
    try { await api.post(`/doctor/peer/requests/${rid}/decline`, { reason }); toast("Request declined."); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (rid) => {
    if (!window.confirm("Cancel this peer consult request?")) return;
    try { await api.post(`/doctor/peer/requests/${rid}/cancel`); toast("Request cancelled."); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div>
          <h3 className="h" style={{ fontSize: 18, marginTop: 0, marginBottom: 4 }}>Peer consultations</h3>
          <p className="lead" style={{ margin: 0 }}>Consult another specialist on the panel — they’ll assign you a slot from outside their patient calendar.</p>
        </div>
        <button className="btn btn-green" onClick={() => setShowAsk(true)} data-testid="doc-peer-new"><UsersRound size={15} /> Request peer consult</button>
      </div>

      <div className="tabs" style={{ margin: "16px 0 12px" }}>
        <button className={`tab ${tab === "incoming" ? "on" : ""}`} onClick={() => setTab("incoming")} data-testid="peer-tab-incoming">Incoming{pendingIn ? ` · ${pendingIn}` : ""}</button>
        <button className={`tab ${tab === "outgoing" ? "on" : ""}`} onClick={() => setTab("outgoing")} data-testid="peer-tab-outgoing">My requests{pendingOut ? ` · ${pendingOut}` : ""}</button>
      </div>

      {list.length === 0 && <p className="lead">No {tab === "incoming" ? "incoming" : "outgoing"} requests yet.</p>}
      {list.map(r => {
        const counterpart = tab === "incoming" ? r.requester : r.target;
        return (
          <div className="peer-req" key={r.id} data-testid={`peer-req-${r.id}`}>
            <div className="pr-head">
              <div>
                <h4>{counterpart.name || "Doctor"}</h4>
                <p className="lead" style={{ margin: 0 }}>{counterpart.specialty}{counterpart.reg_no ? ` · ${counterpart.reg_no}` : ""}</p>
              </div>
              <span className={`pill-mini ${r.status}`}>{r.status}</span>
            </div>
            <p className="pr-reason"><b>Reason:</b> {r.reason}</p>
            {r.preferred_window && <p className="pr-reason"><b>Preferred:</b> {r.preferred_window}</p>}
            {r.status === "scheduled" && <p className="pr-reason" style={{ color: "var(--green-deep, #1c6f4d)" }}><b>Scheduled:</b> {r.scheduled_date} at {r.scheduled_time}</p>}
            {r.status === "declined" && r.decline_reason && <p className="pr-reason"><b>Declined:</b> {r.decline_reason}</p>}
            <div className="pr-actions">
              {tab === "incoming" && r.status === "pending" && (
                <>
                  <button className="btn btn-green btn-sm" onClick={() => onAssignSlot({ requestId: r.id, requesterName: counterpart.name })} data-testid={`peer-assign-${r.id}`}><CheckCircle2 size={13} /> Assign a slot</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDeclineFor(r)} data-testid={`peer-decline-${r.id}`}><X size={13} /> Decline</button>
                </>
              )}
              {tab === "outgoing" && (r.status === "pending" || r.status === "scheduled") && (
                <button className="btn btn-ghost btn-sm" onClick={() => cancel(r.id)} data-testid={`peer-cancel-${r.id}`}><X size={13} /> Cancel</button>
              )}
            </div>
          </div>
        );
      })}

      {showAsk && <PeerAskModal onClose={() => setShowAsk(false)} onSent={() => { setShowAsk(false); load(); }} />}
      {declineFor && <PeerDeclineModal req={declineFor} onClose={() => setDeclineFor(null)} onDone={(reason) => { decline(declineFor.id, reason); setDeclineFor(null); }} />}
    </div>
  );
}

function PeerAskModal({ onClose, onSent }) {
  const [doctors, setDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [pref, setPref] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/doctors/directory").then(r => setDoctors(r.data.doctors || [])); }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return doctors.slice(0, 20);
    return doctors.filter(d => [d.name, d.specialty, d.reg_no, d.email, d.mobile].filter(Boolean).some(v => String(v).toLowerCase().includes(qq))).slice(0, 20);
  }, [doctors, q]);

  const pickKey = useKeyNav(target ? [] : filtered, (d) => setTarget(d), { onEscape: () => setQ("") });

  const send = async () => {
    if (!target) { toast("Please pick a doctor to consult."); return; }
    if (!reason.trim()) { toast("Please write a short reason."); return; }
    setBusy(true);
    try {
      await api.post("/doctor/peer/requests", { target_doctor_id: target.id, reason, preferred_window: pref });
      toast("Request sent ✓");
      onSent();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-shade" onClick={onClose}>
      <div className="modal-card portal-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Request peer consultation</h3>
        <label className="ib-field-label">Search a doctor (name, specialty, NMC, email, mobile)</label>
        <input
          className="ib-lookup-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setTarget(null); }}
          onKeyDown={pickKey.onKeyDown}
          role="combobox"
          aria-expanded={!target && filtered.length > 0}
          aria-activedescendant={!target && pickKey.activeIndex >= 0 && filtered[pickKey.activeIndex] ? `peer-pick-${filtered[pickKey.activeIndex].id}` : undefined}
          placeholder="e.g. cardiology, Dr. Mehta, NMC 12345"
          autoFocus
          data-testid="peer-search"
        />
        {!target && (
          <div className="compose-results" style={{ maxHeight: 200, marginTop: 8 }} role="listbox" ref={pickKey.listRef}>
            {filtered.length === 0 && <p className="compose-results-empty">No doctors match.</p>}
            {filtered.map((d, i) => (
              <button
                key={d.id}
                type="button"
                id={`peer-pick-${d.id}`}
                role="option"
                aria-selected={i === pickKey.activeIndex}
                className={`compose-result-row ${i === pickKey.activeIndex ? "active" : ""}`}
                onMouseEnter={() => pickKey.setActiveIndex(i)}
                onClick={() => setTarget(d)}
                data-testid={`peer-pick-${d.id}`}
              >
                <span className="ib-avatar doctor"><Stethoscope size={14} /></span>
                <span className="compose-result-main"><b>{d.name}</b><small>{d.reg_no || "—"}{d.specialty ? " · " + d.specialty : ""}</small></span>
              </button>
            ))}
            {filtered.length > 0 && <div className="biz-suggest-hint">↑ ↓ to move · ↵ to choose · esc to clear</div>}
          </div>
        )}
        {target && (
          <div className="peer-target-pick" data-testid="peer-target">
            <Stethoscope size={16} /> <b>{target.name}</b> · {target.specialty}
            <button className="x" onClick={() => setTarget(null)} title="Pick another"><X size={14} /></button>
          </div>
        )}
        <label className="ib-field-label" style={{ marginTop: 10 }}>Reason / clinical context</label>
        <textarea className="ib-lookup-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A short summary of why you want their opinion" data-testid="peer-reason" />
        <label className="ib-field-label" style={{ marginTop: 8 }}>Preferred window (optional)</label>
        <input className="ib-lookup-input" value={pref} onChange={(e) => setPref(e.target.value)} placeholder="e.g. this week mornings, any time Thursday" data-testid="peer-pref" />
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-green" onClick={send} disabled={busy} data-testid="peer-send"><Send size={14} /> {busy ? "Sending…" : "Send request"}</button>
        </div>
      </div>
    </div>
  );
}

function PeerDeclineModal({ req, onClose, onDone }) {
  const [reason, setReason] = useState("");
  return (
    <div className="modal-shade" onClick={onClose}>
      <div className="modal-card portal-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Decline request</h3>
        <p className="lead" style={{ marginTop: 0 }}>From {req.requester.name}</p>
        <label className="ib-field-label">Optional message back</label>
        <textarea className="ib-lookup-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Out of town next week — please retry in 10 days" data-testid="peer-decline-reason" />
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={() => onDone(reason)} data-testid="peer-decline-confirm">Decline</button>
        </div>
      </div>
    </div>
  );
}


/* --------------------------- Doctor appt detail -------------------------- */
function DocDetail({ id, user, onBack }) {
  const [appt, setAppt] = useState(null);
  const [notes, setNotes] = useState(""); const [rx, setRx] = useState("");
  const [tab, setTab] = useState("case");

  const load = () => api.get(`/appointments/${id}`).then(r => { setAppt(r.data.appointment); setNotes(r.data.appointment.notes || ""); setRx(r.data.appointment.prescription || ""); });
  useEffect(() => { load(); }, [id]);
  if (!appt) return <p className="lead">Loading…</p>;
  const completed = appt.status === "completed";
  const isVideo = (appt.consult_type || "").toLowerCase().includes("video");

  const save = async (complete) => {
    try { await api.post(`/appointments/${id}/notes`, { notes, prescription: rx, complete }); toast(complete ? "Consultation completed ✓" : "Saved ✓"); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const markNoShow = async (flag) => {
    try { await api.post(`/appointments/${id}/no-show`, { patient_no_show: flag }); toast(flag ? "Marked patient no-show ✓" : "Reverted ✓"); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const viewReport = (fid) => window.open(`${API}/files/${fid}`, "_blank");
  const downloadReport = async (fid, fname) => {
    try {
      const { data } = await api.get(`/files/${fid}/link?download=true`);
      if (data?.url) {
        const a = document.createElement("a"); a.href = data.url; a.download = fname; a.click();
        return;
      }
      const res = await api.get(`/files/${fid}?download=true`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <button className="back" onClick={onBack} data-testid="doc-detail-back"><ArrowLeft size={15} /> Back</button>
      <div className="idcard">
        <div>
          <div className="idname">{appt.patient_name}</div>
          <div className="idspec">{appt.specialty} · {appt.consult_type}</div>
          {(appt.patient_age || appt.patient_sex) && (
            <div className="idspec" style={{ marginTop: 6 }} data-testid="doc-patient-demographics">
              {[appt.patient_age && `Age ${appt.patient_age}`, appt.patient_sex].filter(Boolean).join(" · ")}
            </div>
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span className={`badge ${appt.status}`}>{appt.status}</span>
            <span style={{ color: "#AFC6DE", fontSize: 13 }}><Clock size={13} /> {appt.date} · {appt.time}</span>
            {appt.status === "pending" && (
              <button className="btn btn-ghost btn-sm" onClick={() => markNoShow(true)} data-testid="doc-mark-noshow">Patient didn’t show</button>
            )}
            {appt.status === "missed" && (
              <button className="btn btn-ghost btn-sm" onClick={() => markNoShow(false)} data-testid="doc-undo-noshow">Undo patient no-show</button>
            )}
          </div>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 18 }}>
        <button className={`tab ${tab === "case" ? "on" : ""}`} onClick={() => setTab("case")}>Case & reports</button>
        {isVideo && !completed && <button className={`tab ${tab === "consult" ? "on" : ""}`} onClick={() => setTab("consult")} data-testid="doc-tab-consult">Video & chat</button>}
        <button className={`tab ${tab === "opinion" ? "on" : ""}`} onClick={() => setTab("opinion")} data-testid="doc-tab-opinion">{completed ? "Opinion" : "Write opinion"}</button>
      </div>

      {tab === "case" && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Patient’s concern</h4>
            <p className="lead">{appt.symptom || "—"}</p>
          </div>
          {!completed && <div style={{ marginBottom: 16 }}><AddToCalendar appt={appt} /></div>}
          <div className="card">
            <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Uploaded reports</h4>
            {!completed && <div className="banner info">Reports are view-only until the consultation is completed.</div>}
            {(appt.files || []).length === 0 && <p className="lead">No reports uploaded.</p>}
            {(appt.files || []).map((f, i) => (
              <div className="filechip" key={i}><FileText size={15} /> {f.filename}
                <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => viewReport(f.file_id)} data-testid={`doc-view-${i}`}><Eye size={13} /> View</button>
                  {completed && <button className="btn btn-ghost btn-sm" onClick={() => downloadReport(f.file_id, f.filename)}><Download size={13} /></button>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "consult" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Video room={appt.jitsi_room} />
          <Chat appointmentId={id} user={user} />
        </div>
      )}

      {tab === "opinion" && (
        <div className="card">
          <div className="field"><label>Consultation notes / opinion</label>
            <textarea className="in" rows={5} value={notes} onChange={e => setNotes(e.target.value)} disabled={completed} data-testid="doc-notes" /></div>
          <div className="field"><label>Prescription</label>
            <textarea className="in" rows={4} value={rx} onChange={e => setRx(e.target.value)} disabled={completed} data-testid="doc-rx" /></div>
          {!completed ? (
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => save(false)} data-testid="doc-save-notes"><Save size={15} /> Save draft</button>
              <button className="btn btn-green full" onClick={() => save(true)} data-testid="doc-complete"><CheckCircle2 size={16} /> Mark as completed</button>
            </div>
          ) : <div className="banner info">This consultation is completed. The opinion is available to the patient.</div>}
        </div>
      )}
    </>
  );
}

/* ------------------------------ Doctor dash ------------------------------ */
function MyProfile() {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [clinics, setClinics] = useState([]);
  const reload = () => {
    api.get("/doctor/me/profile").then(r => setP(r.data.profile))
      .catch(e => setErr(formatApiError(e.response?.data?.detail)));
    api.get("/doctor/me/clinics").then(r => setClinics(r.data.clinics || [])).catch(() => setClinics([]));
  };
  useEffect(() => { reload(); }, []);
  if (err) return <div className="banner warn">{err}</div>;
  if (!p) return <p className="lead">Loading…</p>;
  const facts = [
    p.reg_no && { icon: BadgeCheck, label: "NMC Registration number", value: p.reg_no },
    p.qualifications && { icon: GraduationCap, label: "Qualifications", value: p.qualifications },
    p.experience_years && { icon: Briefcase, label: "Experience", value: p.experience_years },
    p.languages && { icon: Languages, label: "Languages", value: p.languages },
    p.consult_fee && { icon: IndianRupee, label: "Consultation fee", value: p.consult_fee },
  ].filter(Boolean);
  return (
    <div data-testid="doc-my-profile">
      <div className="idcard">
        <div className="doc-prof-photo">
          {p.photo_id ? <img src={`${API}/public/image/${p.photo_id}`} alt={p.name} />
            : <UserRound size={34} />}
        </div>
        <div style={{ flex: 1 }}>
          <div className="idname">{p.name}</div>
          <div className="idspec">{p.specialty}{p.reg_no ? ` · ${p.reg_no}` : ""}</div>
          <div style={{ marginTop: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(29,158,117,.18)", color: "#7be3bf", padding: "5px 12px", borderRadius: 100, fontSize: 12.5, fontWeight: 600, textTransform: "capitalize" }}>
              <BadgeCheck size={14} /> {p.approval_status || "—"}
            </span>
          </div>
        </div>
      </div>

      <div className="banner info" style={{ marginBottom: 16 }}>
        Patients only see your name, specialty, NMC registration number, qualifications, experience, languages, consultation fee and bio. Your contact details and documents stay private. Spotted a discrepancy? Contact the admin team and they’ll update it for you.
      </div>

      <h4 className="h" style={{ fontSize: 15, margin: "0 0 8px" }}>Visible to patients</h4>
      {facts.length > 0 && (
        <div className="statgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }} data-testid="doc-profile-facts">
          {facts.map((f, i) => (
            <div className="stat" key={i} style={{ textAlign: "left", display: "flex", gap: 10, alignItems: "center" }}>
              <f.icon size={18} color="#1D9E75" />
              <div><div className="l" style={{ marginBottom: 2 }}>{f.label}</div><div style={{ fontWeight: 600, color: "#042C53" }}>{f.value}</div></div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ marginTop: 16 }}>
        <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>About (visible to patients)</h4>
        <p className="lead" style={{ whiteSpace: "pre-wrap" }}>{p.bio || "No bio added yet. Contact the admin team to add your professional bio."}</p>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>My documents <span className="priv-tag">Private</span></h4>
        <p className="lead" style={{ marginTop: 0, fontSize: 13 }}>Only you and the admin team can see these. If anything is incorrect, please contact the admin to update it.</p>
        {(p.documents || []).length === 0 ? (
          <p className="lead" style={{ margin: 0 }}>No documents on file.</p>
        ) : (
          <div className="doc-files" data-testid="doc-my-documents">
            {p.documents.map((doc, i) => (
              <a key={i} className="doc-file" href={`${API}/files/${doc.file_id}`} target="_blank" rel="noreferrer" data-testid={`doc-file-${i}`}>
                <FileText size={15} /><span>{DOC_LABEL[doc.type] || doc.type}</span><Eye size={14} />
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Contact <span className="priv-tag">Private</span></h4>
        <p className="lead" style={{ margin: 0 }}>Email: {p.email || "—"}</p>
        <p className="lead" style={{ margin: 0 }}>Mobile: {p.mobile || "—"}</p>
      </div>

      <div className="card" style={{ marginTop: 16 }} data-testid="doc-profile-establishments">
        <h4 className="h" style={{ fontSize: 16, marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <Building2 size={16} color="#1D9E75" /> My establishments
        </h4>
        {clinics.length === 0 ? (
          <p className="lead" style={{ margin: 0 }}>No clinics or hospitals added yet. Go to <b>Clinic Consultation</b> to add up to 5 establishments.</p>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {clinics.map((c) => {
              const st = STATUS_STYLE[c.approval_status] || STATUS_STYLE.pending;
              return (
                <div key={c.id} className="estab-mini" data-testid={`doc-profile-estab-${c.id}`}>
                  <span className="estab-icon">{c.establishment_type === "Hospital" ? <Hospital size={16} /> : <Building2 size={16} />}</span>
                  <div style={{ flex: 1 }}>
                    <b>{c.name}</b>
                    <div className="estab-mini-meta">{[c.address, c.pincode].filter(Boolean).join(" · ")}{c.phone ? ` · ${c.phone}` : ""}</div>
                  </div>
                  <span className="estab-status" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <StampUploader profile={p} onUpdate={reload} />
      </div>
    </div>
  );
}

function DocDashboard({ user, onOpen, onAvailability, onInbox, onProfile, onPeer }) {
  const [appts, setAppts] = useState([]);
  const [tab, setTab] = useState("upcoming");
  useEffect(() => { api.get("/appointments").then(r => setAppts(r.data.appointments || [])); }, []);

  const upcoming = appts.filter(a => a.status === "upcoming");
  const completed = appts.filter(a => a.status === "completed");
  const missed = appts.filter(a => a.status === "missed");
  const pending = appts.filter(a => a.status === "pending");
  const list = tab === "upcoming" ? upcoming : tab === "completed" ? completed : tab === "pending" ? pending : missed;

  return (
    <>
      <div className="idcard">
        <div>
          <div className="idname">{user.name}</div>
          <div className="idspec">{user.specialty} · Verified specialist</div>
        </div>
        <div className="idreg">Reg. no.<b>{user.reg_no}</b></div>
      </div>

      <div className="statgrid">
        <div className="stat"><div className="n">{upcoming.length}</div><div className="l">Upcoming</div></div>
        <div className="stat"><div className="n">{completed.length}</div><div className="l">Completed</div></div>
        <div className="stat"><div className="n">{pending.length}</div><div className="l">Pending</div></div>
        <div className="stat"><div className="n">{missed.length}</div><div className="l">Missed</div></div>
      </div>

      <button className="btn btn-ghost full" style={{ margin: "8px 0 20px" }} onClick={onProfile} data-testid="doc-profile-btn"><UserRound size={15} /> View my profile</button>

      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab ${tab === "upcoming" ? "on" : ""}`} onClick={() => setTab("upcoming")} data-testid="doc-tab-upcoming">Upcoming</button>
        <button className={`tab ${tab === "completed" ? "on" : ""}`} onClick={() => setTab("completed")} data-testid="doc-tab-completed">Completed</button>
        <button className={`tab ${tab === "pending" ? "on" : ""}`} onClick={() => setTab("pending")} data-testid="doc-tab-pending">Pending</button>
        <button className={`tab ${tab === "missed" ? "on" : ""}`} onClick={() => setTab("missed")} data-testid="doc-tab-missed">Missed</button>
      </div>

      {list.length === 0 && <p className="lead">No {tab} appointments.</p>}
      {list.map(a => (
        <div className="appt" key={a.id} onClick={() => onOpen(a.id)} data-testid={`doc-appt-${a.id}`}>
          <div className="ic"><Stethoscope size={18} /></div>
          <div className="tm">{a.date}<br />{a.time}</div>
          <div className="m"><h4>{a.patient_name}</h4><p>{a.specialty} · {a.consult_type}{(a.patient_age || a.patient_sex) ? ` · ${[a.patient_age && `Age ${a.patient_age}`, a.patient_sex].filter(Boolean).join(" · ")}` : ""}</p></div>
          <span className={`badge ${a.status}`}>{a.status}</span>
        </div>
      ))}

      <CalendarSubscribe />
    </>
  );
}

/* -------------------------------- Portal --------------------------------- */
/* ------------------------------ Earnings ------------------------------ */
function DocEarnings() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/doctor/me/earnings").then(r => setD(r.data)).catch(() => setD(null)); }, []);
  const downloadCsv = async () => {
    try {
      const res = await api.get("/doctor/me/earnings/statement.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "earnings-statement.csv"; a.click();
    } catch { toast("Could not download statement"); }
  };
  if (!d) return <p className="lead">Loading…</p>;
  const maxA = Math.max(1, ...d.trend.map(t => t.amount));
  const cards = [
    ["Total earnings", `₹${d.total.toLocaleString("en-IN")}`],
    ["Online consults", `₹${d.online_total.toLocaleString("en-IN")} · ${d.online_count}`],
    ["Clinic visits", `₹${d.clinic_total.toLocaleString("en-IN")} · ${d.clinic_count}`],
    ["Avg fee", `₹${d.avg_fee}`],
    ["Completed", d.completed], ["Upcoming", d.upcoming],
  ];
  return (
    <div data-testid="doctor-earnings">
      <div className="estab-header">
        <div>
          <h3 className="h" style={{ fontSize: 20, margin: "0 0 4px" }}>Earnings</h3>
          <p className="lead" style={{ margin: 0 }}>Your consultation revenue across online and in-person visits.</p>
        </div>
        <button className="btn btn-ink" onClick={downloadCsv} data-testid="earnings-download"><Download size={15} /> Download statement</button>
      </div>
      <div className="an-kpis">
        {cards.map(([l, v]) => <div className="an-kpi" key={l} data-testid={`earn-kpi-${l}`}><span>{l}</span><b>{v}</b></div>)}
      </div>
      <div className="card">
        <h4 className="h" style={{ fontSize: 15, marginTop: 0 }}>Revenue — last 6 months</h4>
        <div className="an-bars">
          {d.trend.map(t => (
            <div className="an-bar-col" key={t.month}>
              <div className="an-bar" style={{ height: `${Math.round(100 * t.amount / maxA)}%` }} title={`₹${t.amount}`} />
              <span>{t.month.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        <h4 className="h" style={{ fontSize: 15, marginTop: 0 }}>Recent consultations</h4>
        <table className="appts-table" data-testid="earnings-recent">
          <thead><tr><th>Date</th><th>Patient</th><th>Type</th><th>Fee</th><th>Status</th></tr></thead>
          <tbody>
            {d.recent.map(a => (
              <tr key={a.id}><td>{a.date}</td><td>{a.patient_name}</td><td>{a.consult_type}{a.kind === "clinic" ? " · Clinic" : ""}</td><td>₹{a.fee}</td><td><span className={`badge ${a.status}`}>{a.status}</span></td></tr>
            ))}
            {d.recent.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", color: "#90a4b8" }}>No earnings yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------- Clinic Consultation (Premium) --------------------- */
const STATUS_STYLE = {
  approved: { bg: "#e3f5ec", color: "#0F6E56", label: "Live · Approved" },
  pending: { bg: "#fdf2e3", color: "#9a6a12", label: "Pending admin approval" },
  rejected: { bg: "#fde8e8", color: "#b42318", label: "Rejected — please review" },
};

const EMPTY_ESTAB = {
  name: "", establishment_type: "Clinic", address: "", pincode: "",
  maps_url: "", phone: "", email: "", hours: "", fee: "", slots: {},
};

function SlotEditor({ slots, weekdays, slotTimes, onChange }) {
  const [day, setDay] = useState(weekdays[0] || "Mon");
  const daySlots = slots[day] || [];
  const toggle = (t) => {
    const cur = new Set(slots[day] || []);
    cur.has(t) ? cur.delete(t) : cur.add(t);
    const next = { ...slots, [day]: Array.from(cur).sort() };
    if (next[day].length === 0) delete next[day];
    onChange(next);
  };
  return (
    <div className="estab-slots" data-testid="estab-slot-editor">
      <div className="estab-day-tabs">
        {weekdays.map((d) => {
          const n = (slots[d] || []).length;
          return (
            <button type="button" key={d} className={`estab-day ${day === d ? "on" : ""}`}
              onClick={() => setDay(d)} data-testid={`estab-day-${d}`}>
              {d}{n > 0 && <span className="estab-day-count">{n}</span>}
            </button>
          );
        })}
      </div>
      <div className="estab-times">
        {slotTimes.map((t) => (
          <button type="button" key={t}
            className={`estab-time ${daySlots.includes(t) ? "on" : ""}`}
            onClick={() => toggle(t)} data-testid={`estab-time-${day}-${t}`}>
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

function EstablishmentForm({ initial, meta, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEdit = !!initial.id;

  const submit = async () => {
    if (!form.name.trim()) { toast("Please enter the establishment name"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/doctor/me/clinics/${initial.id}`, form);
      else await api.post("/doctor/me/clinics", form);
      toast(isEdit ? "Establishment updated ✓ — re-sent for approval" : "Establishment added ✓ — sent for approval");
      onSaved();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card estab-form" data-testid="estab-form">
      <div className="row2">
        <div className="field"><label>Establishment name</label>
          <input className="in" value={form.name} onChange={set("name")} placeholder="e.g. Ramesh Oncology Centre" data-testid="estab-name" /></div>
        <div className="field"><label>Type</label>
          <select className="in" value={form.establishment_type} onChange={set("establishment_type")} data-testid="estab-type">
            {(meta.establishment_types || ["Clinic", "Hospital"]).map((t) => <option key={t}>{t}</option>)}
          </select></div>
      </div>
      <div className="field"><label>Full address</label>
        <textarea className="in" rows={2} value={form.address} onChange={set("address")} placeholder="Street, area, city" data-testid="estab-address" /></div>
      <div className="row2">
        <div className="field"><label>PIN code</label>
          <input className="in" value={form.pincode} onChange={set("pincode")} placeholder="e.g. 560038" data-testid="estab-pincode" /></div>
        <div className="field"><label>Google Maps location (link)</label>
          <input className="in" value={form.maps_url} onChange={set("maps_url")} placeholder="https://maps.google.com/…" data-testid="estab-maps" /></div>
      </div>
      <div className="row2">
        <div className="field"><label>Establishment phone <span className="estab-hint-inline">(shown to patients)</span></label>
          <input className="in" value={form.phone} onChange={set("phone")} placeholder="+91 80 1234 5678" data-testid="estab-phone" /></div>
        <div className="field"><label>Establishment email</label>
          <input className="in" value={form.email} onChange={set("email")} placeholder="info@clinic.com" data-testid="estab-email" /></div>
      </div>
      <div className="row2">
        <div className="field"><label>Opening hours (optional)</label>
          <input className="in" value={form.hours} onChange={set("hours")} placeholder="Mon–Sat · 10 AM – 6 PM" data-testid="estab-hours" /></div>
        <div className="field"><label>Consultation fee (₹) <span className="estab-hint-inline">(in-person visit)</span></label>
          <input className="in" value={form.fee} onChange={set("fee")} placeholder="e.g. 1200" data-testid="estab-fee" /></div>
      </div>

      <label className="estab-section-label"><Clock size={14} /> Available clinic slots</label>
      <p className="estab-sub">Pick the times you consult in person at this establishment, day by day.</p>
      <SlotEditor slots={form.slots || {}} weekdays={meta.weekdays || []} slotTimes={meta.slot_times || []}
        onChange={(slots) => setForm({ ...form, slots })} />

      <div className="banner info" style={{ marginTop: 14, fontSize: 12.5 }}>
        <AlertCircle size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
        Your personal phone and email are never shared with patients. Only this establishment's contact details are shown.
      </div>
      <div className="about-footer" style={{ marginTop: 12 }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel} data-testid="estab-cancel"><X size={13} /> Cancel</button>
        <button className="btn btn-green" onClick={submit} disabled={busy} data-testid="estab-save">
          <Save size={15} /> {busy ? "Saving…" : isEdit ? "Save & resubmit" : "Add establishment"}
        </button>
      </div>
    </div>
  );
}

function EstablishmentCard({ clinic, onEdit, onDelete, onReload }) {
  const st = STATUS_STYLE[clinic.approval_status] || STATUS_STYLE.pending;
  const photoInput = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    api.post(`/doctor/me/clinics/${clinic.id}/photos`, fd)
      .then(() => { toast("Photos uploaded ✓ — re-sent for approval"); onReload(); })
      .catch((err) => toast(formatApiError(err.response?.data?.detail)));
  };
  const delPhoto = (fid) => {
    api.delete(`/doctor/me/clinics/${clinic.id}/photos/${fid}`)
      .then(() => { toast("Photo removed"); onReload(); })
      .catch((err) => toast(formatApiError(err.response?.data?.detail)));
  };
  const slotDays = Object.keys(clinic.slots || {}).filter((d) => (clinic.slots[d] || []).length);
  return (
    <div className="card estab-card" data-testid={`estab-card-${clinic.id}`}>
      <div className="estab-card-head">
        <span className="estab-icon">{clinic.establishment_type === "Hospital" ? <Hospital size={20} /> : <Building2 size={20} />}</span>
        <div style={{ flex: 1 }}>
          <h4 className="estab-card-name">{clinic.name} <span className="estab-type-chip">{clinic.establishment_type}</span></h4>
          <span className="estab-status" style={{ background: st.bg, color: st.color }} data-testid={`estab-status-${clinic.id}`}>{st.label}</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="pid-mini" title="Edit" onClick={onEdit} data-testid={`estab-edit-${clinic.id}`}><Pencil size={14} /></button>
          <button className="pid-mini" title="Delete" onClick={onDelete} data-testid={`estab-delete-${clinic.id}`}><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="estab-facts">
        {clinic.address && <span><MapPin size={13} /> {clinic.address}{clinic.pincode ? ` · ${clinic.pincode}` : ""}</span>}
        {clinic.phone && <span><Phone size={13} /> {clinic.phone}</span>}
        {clinic.email && <span><Mail size={13} /> {clinic.email}</span>}
        {clinic.hours && <span><Clock size={13} /> {clinic.hours}</span>}
        {clinic.fee && <span><span style={{ fontWeight: 600 }}>₹{String(clinic.fee).replace(/\D/g, "")}</span> consult fee</span>}
        {clinic.maps_url && <a href={clinic.maps_url} target="_blank" rel="noreferrer"><ExternalLink size={13} /> Map</a>}
      </div>
      {slotDays.length > 0 && (
        <div className="estab-slot-summary">
          {slotDays.map((d) => <span key={d} className="estab-slot-chip">{d}: {(clinic.slots[d] || []).join(", ")}</span>)}
        </div>
      )}
      <div className="estab-photos">
        {(clinic.photos || []).map((fid) => (
          <div className="estab-photo" key={fid}>
            <img src={`${API}/public/image/${fid}`} alt="" />
            <button className="estab-photo-del" onClick={() => delPhoto(fid)} data-testid={`estab-photo-del-${fid}`}><X size={12} /></button>
          </div>
        ))}
        <label className="estab-photo-add" data-testid={`estab-photo-add-${clinic.id}`}>
          <ImageIcon size={16} /><span>Add photos</span>
          <input type="file" accept="image/*" multiple hidden onChange={photoInput} />
        </label>
      </div>
    </div>
  );
}

function DoctorClinicConsultation({ user, refresh }) {
  const [meta, setMeta] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [subBusy, setSubBusy] = useState(false);

  const load = () => api.get("/doctor/me/clinics").then((r) => setMeta(r.data)).catch(() => setMeta({ clinics: [], subscribed: false }));
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setSubBusy(true);
    try { await api.post("/doctor/subscribe"); toast("Premium activated ✓"); await refresh?.(); await load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setSubBusy(false); }
  };
  const remove = async (cid) => {
    if (!window.confirm("Delete this establishment? This cannot be undone.")) return;
    try { await api.delete(`/doctor/me/clinics/${cid}`); toast("Establishment deleted"); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  if (!meta) return <p className="lead">Loading…</p>;

  const subscribed = meta.subscribed || user?.subscribed;
  if (!subscribed) {
    return (
      <div data-testid="doctor-clinic-page">
        <div className="card" style={{ textAlign: "center", padding: "40px 24px" }}>
          <span className="ib-avatar doctor" style={{ width: 56, height: 56, margin: "0 auto 16px", display: "inline-flex" }}><Building2 size={26} /></span>
          <span className="rel-chip" style={{ background: "#fdf2e3", color: "#9a6a12", marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 6 }}><Sparkles size={13} /> Premium subscription</span>
          <h3 className="h" style={{ fontSize: 22, margin: "8px 0 6px" }}>Clinic Consultation</h3>
          <p className="lead" style={{ maxWidth: 480, margin: "0 auto" }}>
            Subscribe to Premium to list your clinics and hospitals on 2nd Opinion. Add up to 5 establishments with address, contact details, photos and in-person consultation slots — visible to premium patients near you once approved.
          </p>
          <div style={{ marginTop: 20 }}>
            <button type="button" className="btn btn-green" onClick={subscribe} disabled={subBusy} data-testid="doctor-clinic-subscribe-btn">
              <Sparkles size={15} /> {subBusy ? "Activating…" : "Subscribe Now"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const clinics = meta.clinics || [];
  return (
    <div data-testid="doctor-clinic-page">
      <div className="estab-header">
        <div>
          <h3 className="h" style={{ fontSize: 20, margin: "0 0 4px" }}>My establishments</h3>
          <p className="lead" style={{ margin: 0 }}>List your clinics & hospitals ({clinics.length}/{meta.limit}). Each is reviewed by our team before going live to patients.</p>
        </div>
        {clinics.length < meta.limit && !adding && !editing && (
          <button className="btn btn-green" onClick={() => setAdding(true)} data-testid="estab-add-btn"><Plus size={15} /> Add establishment</button>
        )}
      </div>

      {adding && <EstablishmentForm initial={{ ...EMPTY_ESTAB }} meta={meta} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {editing && <EstablishmentForm initial={editing} meta={meta} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {!adding && !editing && clinics.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: 28 }}>
          <Building2 size={28} color="#1D9E75" style={{ margin: "0 auto 8px" }} />
          <p className="lead">You haven't added any establishment yet. Add your first clinic or hospital to appear in patient clinic-search.</p>
        </div>
      )}

      {!adding && !editing && clinics.map((c) => (
        <EstablishmentCard key={c.id} clinic={c} onEdit={() => setEditing(c)} onDelete={() => remove(c.id)} onReload={load} />
      ))}
    </div>
  );
}

/* ----------------- CS-onboarded doctor: set own password ----------------- */
function firstName(name = "") {
  return name.replace(/^(Dr\.?|Mr\.?|Mrs\.?|Ms\.?|Prof\.?)\s+/i, "").trim().split(" ")[0] || name;
}

function DoctorForcePassword({ user, onDone, onSignOut }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-own-password", { new_password: pw });
      toast("Password set ✓");
      await onDone();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="force-pw-wrap" data-testid="doctor-force-set-password">
      <div className="force-pw-card">
        <div className="force-pw-ic"><ShieldCheck size={22} /></div>
        <h2>Set your own password</h2>
        <p className="lead">Welcome, {firstName(user.name)}! Your account was created by our customer support team. For your security, please set your own password before continuing to upload your documents.</p>
        {err && <p className="err" data-testid="doctor-force-pw-error">{err}</p>}
        <div className="field"><label>New password (min 8 chars)</label>
          <input className="in" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="doctor-force-pw-new" />
        </div>
        <div className="field"><label>Confirm password</label>
          <input className="in" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="doctor-force-pw-confirm" />
        </div>
        <button className="btn btn-ink full" onClick={submit} disabled={busy} data-testid="doctor-force-pw-submit">
          <Lock size={15} /> {busy ? "Setting…" : "Set password & continue"}
        </button>
        <p className="note" style={{ cursor: "pointer", marginTop: 12 }} onClick={onSignOut} data-testid="doctor-force-pw-signout">Sign out</p>
      </div>
    </div>
  );
}

/* --------- CS-onboarded doctor: upload documents / await approval --------- */
function DoctorOnboarding({ user, onSubmitted, onSignOut }) {
  const [files, setFiles] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const hasDocs = (user.documents || []).length > 0;

  const submitDocs = async () => {
    setErr("");
    const required = DOC_FIELDS.filter((d) => d.req).map((d) => d.key);
    if (required.some((k) => !files[k])) { setErr("Please upload all required documents (Identity, Degree, MBBS Registration)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(files).forEach(([k, f]) => { if (f) { fd.append("files", f); fd.append("types", k); } });
      await api.post("/auth/doctor-documents-auth", fd);
      toast("Documents submitted ✓");
      await onSubmitted();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (hasDocs || user.docs_submitted) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: "30px auto", textAlign: "center" }} data-testid="doctor-onboarding-pending">
        <CheckCircle2 size={42} color="#1D9E75" />
        <h2 className="h" style={{ fontSize: 22 }}>Application submitted</h2>
        <p className="lead">Thank you, {firstName(user.name)}. Your documents are with our team for verification. You&apos;ll get full access to your portal once an admin approves your account.</p>
        <button className="btn btn-ghost" onClick={onSignOut} data-testid="doctor-onboarding-signout"><LogOut size={15} /> Sign out</button>
      </div>
    );
  }

  return (
    <div className="auth-form" style={{ maxWidth: 620, margin: "20px auto" }} data-testid="doctor-onboarding-docs">
      <h2 className="h" style={{ fontSize: 22, marginTop: 0 }}>Upload your documents</h2>
      <p className="lead" style={{ marginBottom: 14 }}>Welcome, {firstName(user.name)}. Please upload your credentials to apply for approval. Our team will verify them before activating your account.</p>
      {err && <p className="err" data-testid="doctor-onboarding-error">{err}</p>}
      {DOC_FIELDS.map((d) => (
        <div className="docrow" key={d.key}>
          <FileText size={18} color="#185FA5" />
          <div className="m"><h4>{d.label}<span className={`req ${d.req ? "r" : "o"}`}>{d.req ? "required" : "optional"}</span></h4>
            <p>{files[d.key] ? files[d.key].name : "No file chosen"}</p></div>
          <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
            <Upload size={13} /> {files[d.key] ? "Change" : "Upload"}
            <input type="file" hidden onChange={(e) => setFiles({ ...files, [d.key]: e.target.files[0] })} data-testid={`doctor-onboarding-file-${d.key}`} />
          </label>
        </div>
      ))}
      <button className="btn btn-ink full" style={{ marginTop: 10 }} onClick={submitDocs} disabled={busy} data-testid="doctor-onboarding-submit">{busy ? "Submitting…" : "Submit for verification"}</button>
      <p className="note" style={{ cursor: "pointer", marginTop: 12 }} onClick={onSignOut} data-testid="doctor-onboarding-signout-2">Sign out</p>
    </div>
  );
}

export default function DoctorPortal() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const { summary } = useMessaging();
  const [view, setView] = useState("dashboard");
  const [activeId, setActiveId] = useState(null);
  const [convId, setConvId] = useState(null);
  const [assignCtx, setAssignCtx] = useState(null);
  const [sidebar, setSidebar] = useState(false);
  const [navGroupsOpen, setNavGroupsOpen] = useState({ communications: true });
  const ribbon = <>Doctor portal · <b>Verified specialist panel</b></>;
  const TITLES = { dashboard: "Dashboard", appointments: "My appointments", profile: "My profile", availability: "Availability", clinic: "Clinic Consultation", peer: "Peer consultations", "peer-assign": "Assign peer slot", inbox: "Inbox", mail: "Mail", detail: "Appointment" };

  if (user === undefined) return <PortalShell ribbon="Doctor portal" title="2nd Opinion" sub="Doctor portal"><p className="lead">Loading…</p></PortalShell>;

  if (!user || user.role !== "doctor") {
    return (
      <div className="doc-auth-scope">
        <PortalShell ribbon={ribbon} title="2nd Opinion" sub="Doctor portal">
          {user && user.role !== "doctor"
            ? <div className="banner warn">You’re signed in as {user.role}. <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={logout}>Sign out</span> to use the doctor portal.</div>
            : <DoctorAuth onDone={() => setView("dashboard")} />}
        </PortalShell>
      </div>
    );
  }

  // CS-onboarded doctor must set their own password before doing anything else.
  if (user.must_set_password) {
    return (
      <PortalShell ribbon={ribbon} title="2nd Opinion" sub="Doctor portal">
        <DoctorForcePassword user={user} onDone={refresh} onSignOut={logout} />
      </PortalShell>
    );
  }

  // Doctor signed in but not yet approved (CS onboarding): upload docs / await approval.
  if (user.approval_status !== "approved") {
    return (
      <PortalShell ribbon={ribbon} title="2nd Opinion" sub="Doctor portal">
        <DoctorOnboarding user={user} onSubmitted={refresh} onSignOut={logout} />
      </PortalShell>
    );
  }

  const openInbox = (cid) => { setConvId(cid); setView("inbox"); };
  const bell = (
    <NotificationsBell
      count={summary.unread}
      kinds={["admin_doctor", "patient_doctor"]}
      color="#1D9E75"
      onOpen={openInbox}
      testid="doctor-bell"
      label="Notifications"
    />
  );

  const NAV = [
    { type: "item", key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { type: "item", key: "appointments", label: "My appointments", icon: ListChecks },
    { type: "item", key: "profile", label: "My profile", icon: UserRound },
    { type: "item", key: "availability", label: "Availability", icon: CalendarX },
    { type: "item", key: "earnings", label: "Earnings", icon: BadgeCheck },
    { type: "item", key: "clinic", label: "Clinic Consultation", icon: Building2 },
    { type: "item", key: "peer", label: "Peer consultations", icon: UsersRound },
    {
      type: "group", key: "communications", label: "Communications", icon: MessageSquare, children: [
        { key: "inbox", label: "Inbox", icon: MessageSquare },
        { key: "mail", label: "Mail", icon: Megaphone },
      ],
    },
  ];
  const activeKey = view === "detail" ? "appointments" : view === "peer-assign" ? "peer" : view;
  const toggleGroup = (key) => setNavGroupsOpen((g) => ({ ...g, [key]: !g[key] }));
  // Inbox/Communications badge should reflect ONLY chat conversation unreads, not per-appointment private notes.
  const convUnread = Math.max(0, (summary.unread || 0) - (summary.appt_notes || 0));

  return (
    <div className={`portal doc-shell ${sidebar ? "side-open" : ""}`} data-testid="doctor-portal">
      <aside className="doc-side doc-side-flush" data-testid="doctor-sidebar">
        <div className="doc-side-brand">
          <div className="doc-side-logo">2</div>
          <div><h1>2nd Opinion</h1><p>Doctor portal</p></div>
        </div>
        <div className="doc-side-id">
          <span className="doc-side-reg" data-testid="doc-side-reg">Reg. {user.reg_no}</span>
          <b>{user.name}</b>
          <small>{user.specialty} · Verified specialist</small>
        </div>
        <nav>
          {NAV.map((n) => {
            if (n.type === "item") {
              const Icon = n.icon;
              return (
                <button
                  key={n.key}
                  className={`doc-nav ${activeKey === n.key ? "on" : ""}`}
                  onClick={() => { setView(n.key); setSidebar(false); }}
                  data-testid={`doc-nav-${n.key}`}
                >
                  <Icon size={16} /> {n.label}
                </button>
              );
            }
            // group
            const Icon = n.icon;
            const hasActive = n.children.some((c) => c.key === activeKey);
            const isOpen = !!navGroupsOpen[n.key] || hasActive;
            return (
              <div className={`doc-nav-group ${isOpen ? "open" : ""}`} key={n.key}>
                <button
                  className={`doc-nav doc-nav-group-btn ${hasActive ? "on-soft" : ""}`}
                  onClick={() => toggleGroup(n.key)}
                  aria-expanded={isOpen}
                  data-testid={`doc-nav-group-${n.key}`}
                >
                  <Icon size={16} /> <span style={{ flex: 1, textAlign: "left" }}>{n.label}</span>
                  {convUnread > 0 && <span className="doc-nav-badge" data-testid="doc-nav-comm-badge">{convUnread}</span>}
                  <ChevronDown size={14} style={{ transition: "transform 160ms ease", transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)" }} />
                </button>
                {isOpen && (
                  <div className="doc-nav-children">
                    {n.children.map((c) => {
                      const CIcon = c.icon;
                      return (
                        <button
                          key={c.key}
                          className={`doc-nav doc-nav-child ${activeKey === c.key ? "on" : ""}`}
                          onClick={() => { c.key === "inbox" ? openInbox(null) : setView(c.key); setSidebar(false); }}
                          data-testid={`doc-nav-${c.key}`}
                        >
                          <CIcon size={14} /> {c.label}
                          {c.key === "inbox" && convUnread > 0 && <span className="doc-nav-badge" data-testid="doc-nav-inbox-badge">{convUnread}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="doc-side-foot">
          <a onClick={() => nav("/")} data-testid="doctor-view-website"><Home size={15} /> View website</a>
          <a onClick={() => logout()} data-testid="doctor-signout"><LogOut size={15} /> Sign out</a>
        </div>
      </aside>

      {sidebar && <div className="doc-side-scrim" onClick={() => setSidebar(false)} />}

      <div className="doc-main-area">
        <div className="doc-topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="doc-burger" onClick={() => setSidebar((s) => !s)} data-testid="doctor-burger"><MenuIcon size={18} /></button>
            <h2>{TITLES[view] || "Doctor portal"}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {bell}
            <span className="doc-topbar-name">{user.name}</span>
          </div>
        </div>

        <main className="doc-content">
          <div className="doc-main">
          {view === "dashboard" && <DocDashboard user={user} onOpen={(id) => { setActiveId(id); setView("detail"); }} onAvailability={() => setView("availability")} onInbox={openInbox} onProfile={() => setView("profile")} onPeer={() => setView("peer")} onAllAppts={() => setView("appointments")} />}
          {view === "appointments" && <DoctorMyAppointments />}
          {view === "profile" && <MyProfile />}
          {view === "availability" && <Availability />}
          {view === "earnings" && <DocEarnings />}
          {view === "clinic" && <DoctorClinicConsultation user={user} refresh={refresh} />}
          {view === "peer" && <PeerConsult onAssignSlot={(ctx) => { setAssignCtx(ctx); setView("peer-assign"); }} />}
          {view === "peer-assign" && assignCtx && (<><button className="back" onClick={() => { setAssignCtx(null); setView("peer"); }} data-testid="peer-assign-back"><ArrowLeft size={15} /> Back to peer requests</button><Availability assignContext={assignCtx} onAssigned={() => { setAssignCtx(null); setView("peer"); }} /></>)}
          {view === "detail" && <DocDetail id={activeId} user={user} onBack={() => setView("dashboard")} />}
          {view === "inbox" && <Inbox role="doctor" initialConversationId={convId} accent="#1D9E75" />}
          {view === "mail" && <MailView role="doctor" accent="#1D9E75" />}
          </div>
        </main>
      </div>
    </div>
  );
}
